import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SortableHeader } from "@/components/dataTable/SortableHeader";
import type { SortState } from "@/components/dataTable/tableSort";
import { AutoResizeTextarea } from "@/components/ui/AutoResizeTextarea";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { FieldRow, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { TimesheetFlowFooter, TimesheetFlowFooterButton } from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { TimesheetFlowCard } from "@/features/timesheetFlow/components/TimesheetFlowCard";
import { TimesheetMaterialRow } from "@/features/timesheetFlow/components/TimesheetMaterialRow";
import { TimesheetMaterialDialog } from "@/features/timesheetFlow/components/TimesheetMaterialDialog";
import {
  TimesheetEditorTable,
  TimesheetEditorTableBody,
  TimesheetEditorTableFoot,
  TimesheetEditorTableHead,
  TimesheetEditorTableHeadCell,
  TimesheetEditorTableShell,
} from "@/features/timesheetFlow/components/TimesheetEditorTable";
import { TimesheetTimeSortBar } from "@/features/timesheetFlow/components/TimesheetTimeSortBar";
import { TimesheetTimeRowDialog } from "@/features/timesheetFlow/components/TimesheetTimeRowDialog";
import { TimesheetTimeRowDisplay } from "@/features/timesheetFlow/components/TimesheetTimeRowDisplay";
import { WorkDescriptionAssistPanel } from "@/features/timesheetFlow/components/WorkDescriptionAssistPanel";
import { TimesheetInlineSuggestionPanel } from "@/features/timesheetFlow/components/TimesheetInlineSuggestionPanel";
import type { SuggestionItem } from "@/features/timesheetFlow/components/SuggestionPickerDialog";
import { useTimesheetEditorState } from "@/features/timesheetFlow/hooks/useTimesheetEditorState";
import { useWorkDescriptionAssist } from "@/features/timesheetFlow/hooks/useWorkDescriptionAssist";
import {
  buildHeroSuggestionItems,
  collectHeroUsageFromRows,
  importHeroTimesToRows,
} from "@/features/timesheetFlow/lib/heroTimesImport";
import {
  aggregateHeroTimesLegacy,
  ensureHeroEditableBaseline,
  prepareHeroTimesForSuggestions,
} from "@/features/timesheetFlow/lib/heroTimesAggregation";
import {
  buildOrderedMaterialSuggestionItems,
  importMaterialSuggestions,
  loadStandardMaterialsForProject,
  materialSuggestionValue,
  validateMaterialsForReady,
} from "@/features/timesheetFlow/lib/materialSuggestions";
import {
  buildAppTimeSuggestionItems,
  importAppTimesToRows,
} from "@/features/timesheetFlow/lib/employeeTimesImport";
import {
  isNwPlusFpAuftragsart,
  resolveProjectAuftragsart,
} from "@/features/timesheetFlow/lib/auftragsart";
import { formatTimesheetOrt } from "@/features/timesheetFlow/lib/timesheetProjectPicker";
import {
  DEFAULT_TIMESHEET_TIME_SORT,
  formatTimesheetHours,
  mergeTimesheetDayEntries,
  normalizeTimesheetTimeSort,
  sortTimesheetTimeEntries,
  sumTimesheetHours,
  toggleTimesheetTimeSort,
} from "@/features/timesheetFlow/lib/timesheetTimeSort";
import { TimesheetFinalizeFlow } from "@/features/timesheetFlow/components/TimesheetFinalizeFlow";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import {
  createTimesheet,
  fetchTimesheetPreviewPdf,
  getAppTimes,
  getAppTimeUsage,
  getFestpreisMaterialSuggestions,
  getHeroTimes,
  getHeroUsage,
  getOrderedMaterials,
  getTimesheetDetail,
  getKundeSettings,
  getOrderForTimesheet,
  postHeroUsage,
  updateTimesheet,
  type OrderedMaterialSuggestion,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetMaterialRecord, TimesheetProjectPreset, TimesheetRowRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { getProjectCustomFieldSchemas } from "@/lib/projects";

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

export function TimesheetEditorScreen({
  project: initialProject,
  timesheetId: initialTimesheetId,
  auftragsart,
  onSaved,
  onOpenActions,
  onOpenDetail,
  onBack,
  onCancel,
  onChangeProject,
}: {
  project: TimesheetProjectPreset;
  timesheetId?: number | null;
  auftragsart: string | null;
  onSaved: (timesheetId: number) => void;
  onOpenActions?: (timesheetId: number) => void;
  onOpenDetail?: (timesheetId: number) => void;
  onBack: () => void;
  onCancel: () => void;
  onChangeProject?: () => void;
}) {
  const editor = useTimesheetEditorState();
  const [loading, setLoading] = useState(Boolean(initialTimesheetId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedAuftragsart, setResolvedAuftragsart] = useState<string | null>(auftragsart);
  const effectiveAuftragsart = auftragsart ?? resolvedAuftragsart;
  const [showTimeRange, setShowTimeRange] = useState(true);
  const [useTravelEighths, setUseTravelEighths] = useState(false);
  const [customerSettingsReady, setCustomerSettingsReady] = useState(false);
  const heroLegacyAggregatesRef = useRef<ReturnType<typeof aggregateHeroTimesLegacy>>([]);
  const orderedMaterialsRef = useRef<OrderedMaterialSuggestion[]>([]);
  const festpreisMaterialsRef = useRef<OrderedMaterialSuggestion[]>([]);
  const rowsRef = useRef(editor.rows);
  rowsRef.current = editor.rows;
  const heroLocallyFreedRef = useRef(editor.heroLocallyFreed);
  heroLocallyFreedRef.current = editor.heroLocallyFreed;
  const employeeTimeLocallyFreedRef = useRef(editor.employeeTimeLocallyFreed);
  employeeTimeLocallyFreedRef.current = editor.employeeTimeLocallyFreed;
  const [standardMaterialHint, setStandardMaterialHint] = useState<string | null>(null);
  const standardMaterialsLoadedRef = useRef<number | null>(null);
  const [suggestionReloadKey, setSuggestionReloadKey] = useState(0);
  const [timeSort, setTimeSort] = useState<SortState>(DEFAULT_TIMESHEET_TIME_SORT);
  const [finalizeTimesheetId, setFinalizeTimesheetId] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [timeRowDialog, setTimeRowDialog] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  });
  const [materialDialog, setMaterialDialog] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  });
  const workDescriptionAssist = useWorkDescriptionAssist(editor.workDescription);
  const layers = useTimesheetFlowLayers();

  const timesheetId = editor.timesheet?.id ?? initialTimesheetId ?? null;

  useEffect(() => {
    editor.setProject(initialProject);
  }, [initialProject]);

  useEffect(() => {
    if (auftragsart) {
      setResolvedAuftragsart(auftragsart);
      return;
    }
    let cancelled = false;
    void getProjectCustomFieldSchemas()
      .then((schemas) => resolveProjectAuftragsart(initialProject, schemas))
      .then((art) => {
        if (!cancelled) setResolvedAuftragsart(art);
      })
      .catch(() => {
        if (!cancelled) setResolvedAuftragsart(null);
      });
    return () => {
      cancelled = true;
    };
  }, [auftragsart, initialProject]);

  useEffect(() => {
    if (!initialTimesheetId) return;
    setLoading(true);
    void getTimesheetDetail(initialTimesheetId)
      .then((bundle) => {
        editor.setTimesheet(bundle.timesheet);
        editor.setRows(bundle.rows);
        editor.setMaterials(bundle.materials);
        editor.setWorkDescription(bundle.timesheet.work_description ?? "");
        setTimeSort(
          normalizeTimesheetTimeSort(
            bundle.timesheet.time_rows_sort_key,
            bundle.timesheet.time_rows_sort_dir,
          ),
        );
        workDescriptionAssist.resetForLoadedText(bundle.timesheet.work_description ?? "");
        editor.setDateFrom(bundle.timesheet.date_from ?? "");
        editor.setDateTo(bundle.timesheet.date_to ?? "");
        editor.setTravelKm(bundle.timesheet.travel_km != null ? String(bundle.timesheet.travel_km) : "");
        editor.setTravelEighths(
          bundle.timesheet.travel_eighths != null ? String(bundle.timesheet.travel_eighths) : "",
        );
        editor.setTravelEighthsOverridden(Boolean(bundle.timesheet.travel_eighths_overridden));
        const editableUuids = new Set<string>();
        for (const row of bundle.rows) {
          for (const uuid of row.hero_uuids ?? (row.hero_uuid ? [row.hero_uuid] : [])) {
            editableUuids.add(uuid);
          }
        }
        editor.setHeroEditableInThisTimesheet(editableUuids);
        const editableAppTimeIds = new Set<number>();
        for (const row of bundle.rows) {
          if (row.employee_time_id != null) {
            editableAppTimeIds.add(Number(row.employee_time_id));
          }
        }
        editor.setEmployeeTimeEditableInThisTimesheet(editableAppTimeIds);
        if (bundle.timesheet.show_time_range_on_timesheet != null) {
          setShowTimeRange(Boolean(bundle.timesheet.show_time_range_on_timesheet));
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Laden fehlgeschlagen.");
      })
      .finally(() => setLoading(false));
  }, [initialTimesheetId]);

  useEffect(() => {
    let cancelled = false;
    setCustomerSettingsReady(false);

    void (async () => {
      let kundenId = initialProject.kunden_id ?? null;
      if (!kundenId && initialProject.id) {
        try {
          const order = await getOrderForTimesheet(initialProject.id);
          kundenId = order.kunden_id ?? null;
        } catch {
          /* optional */
        }
      }

      if (!kundenId) {
        if (!cancelled) {
          setShowTimeRange(true);
          setUseTravelEighths(false);
          setCustomerSettingsReady(true);
        }
        return;
      }

      try {
        const k = await getKundeSettings(kundenId);
        if (!cancelled) {
          setShowTimeRange(k.show_time_range_on_timesheet ?? true);
          setUseTravelEighths(Boolean(k.use_travel_eighths));
        }
      } catch {
        if (!cancelled) {
          setShowTimeRange(true);
          setUseTravelEighths(false);
        }
      } finally {
        if (!cancelled) setCustomerSettingsReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialProject.kunden_id, initialProject.id]);

  useEffect(() => {
    if (initialTimesheetId) return;
    if (standardMaterialsLoadedRef.current === initialProject.id) return;
    standardMaterialsLoadedRef.current = initialProject.id;
    setStandardMaterialHint("Standardmaterialien werden geladen …");
    void loadStandardMaterialsForProject(initialProject)
      .then(({ materials, source }) => {
        if (materials.length) {
          editor.appendMaterials(materials);
          setStandardMaterialHint(
            source === "sordner"
              ? "Standardmaterialien des S-Ordners wurden übernommen."
              : source === "ordner"
                ? "Standardmaterialien des Ordners wurden übernommen."
                : "Standardmaterialien des Kunden wurden übernommen.",
          );
        } else {
          setStandardMaterialHint("Keine Standardmaterialien hinterlegt.");
        }
      })
      .catch(() => {
        setStandardMaterialHint("Standardmaterialien konnten nicht geladen werden.");
      });
  }, [initialProject.id, initialTimesheetId]);

  useEffect(() => {
    editor.syncPeriodFromRows();
  }, [editor.rows]);

  useEffect(() => {
    if (!useTravelEighths || editor.travelEighthsOverridden) return;
    editor.applyTravelEighthsFromRows();
  }, [editor.rows, useTravelEighths, editor.travelEighthsOverridden]);

  useEffect(() => {
    if (!customerSettingsReady) return;
    setSuggestionReloadKey((k) => k + 1);
  }, [customerSettingsReady, showTimeRange]);

  useEffect(() => {
    if (!showTimeRange && (timeSort.key === "time_from" || timeSort.key === "time_to")) {
      setTimeSort(DEFAULT_TIMESHEET_TIME_SORT);
    }
  }, [showTimeRange, timeSort.key]);

  const sortedTimeEntries = useMemo(() => {
    const indexed = editor.rows.map((row, index) => ({
      row,
      index,
      sourceIndices: [index],
    }));
    const prepared = showTimeRange ? indexed : mergeTimesheetDayEntries(indexed);
    return sortTimesheetTimeEntries(prepared, timeSort);
  }, [editor.rows, timeSort, showTimeRange]);
  const totalHours = useMemo(() => sumTimesheetHours(editor.rows), [editor.rows]);
  const totalHoursLabel = `${formatTimesheetHours(totalHours)} Std.`;

  const heroUsedInDraft = useMemo(() => {
    const used = new Set<string>();
    for (const row of editor.rows) {
      if (row.hero_uuid) used.add(row.hero_uuid);
      if (row.hero_row_key) used.add(row.hero_row_key);
    }
    return used;
  }, [editor.rows]);

  const appTimeUsedInDraft = useMemo(() => {
    const used = new Set<string>();
    for (const row of editor.rows) {
      if (row.employee_time_id != null) {
        used.add(String(row.employee_time_id));
      }
    }
    return used;
  }, [editor.rows]);

  const appTimeLocallyFreed = useMemo(
    () => new Set([...editor.employeeTimeLocallyFreed].map(String)),
    [editor.employeeTimeLocallyFreed],
  );

  const materialsSuggestionValuesInDraft = useMemo(
    () =>
      new Set(
        editor.materials
          .map((m) => materialSuggestionValue(m))
          .filter((value): value is string => Boolean(value)),
      ),
    [editor.materials],
  );

  const fetchOrderedMaterialItems = useCallback(async (): Promise<SuggestionItem[]> => {
    const list = await getOrderedMaterials(initialProject.id);
    orderedMaterialsRef.current = list;
    return buildOrderedMaterialSuggestionItems(list, timesheetId);
  }, [initialProject.id, timesheetId]);

  const onOrderedMaterialImport = (ids: string[]) => {
    const added = importMaterialSuggestions(
      orderedMaterialsRef.current,
      new Set(ids),
      timesheetId,
      editor.materials,
    );
    if (added.length) {
      editor.appendMaterials(added);
    }
  };

  const showFestpreisSuggestions = isNwPlusFpAuftragsart(effectiveAuftragsart);

  const fetchFestpreisMaterialItems = useCallback(async (): Promise<SuggestionItem[]> => {
    if (!showFestpreisSuggestions) return [];
    const list = await getFestpreisMaterialSuggestions(initialProject.id);
    festpreisMaterialsRef.current = list;
    return buildOrderedMaterialSuggestionItems(list, timesheetId);
  }, [initialProject.id, timesheetId, showFestpreisSuggestions]);

  const onFestpreisMaterialImport = (ids: string[]) => {
    const added = importMaterialSuggestions(
      festpreisMaterialsRef.current,
      new Set(ids),
      timesheetId,
      editor.materials,
    );
    if (added.length) {
      editor.appendMaterials(added);
    }
  };

  const fetchHeroItems = useCallback(async (): Promise<SuggestionItem[]> => {
    if (!customerSettingsReady) return [];
    const hp = initialProject.hero_project_id;
    if (!hp) return [];
    const [rawTimes, usage] = await Promise.all([
      getHeroTimes(hp),
      getHeroUsage(initialProject.id),
    ]);

    const legacyAggregates = aggregateHeroTimesLegacy(rawTimes, showTimeRange);
    heroLegacyAggregatesRef.current = legacyAggregates;

    const editableBaseline = ensureHeroEditableBaseline(
      rowsRef.current,
      legacyAggregates,
      editor.heroEditableInThisTimesheet,
    );

    const { items } = prepareHeroTimesForSuggestions(rawTimes, {
      auftragsart: effectiveAuftragsart,
      showTimeRange,
      usage,
      editableInThisTimesheet: editableBaseline,
    });

    const usedInDraft = new Set<string>();
    for (const row of rowsRef.current) {
      if (row.hero_uuid) usedInDraft.add(row.hero_uuid);
      if (row.hero_row_key) usedInDraft.add(row.hero_row_key);
    }

    return buildHeroSuggestionItems(items, {
      auftragsart: effectiveAuftragsart,
      usage,
      editableInThisTimesheet: editableBaseline,
      locallyFreed: heroLocallyFreedRef.current,
      usedInCurrentDraft: usedInDraft,
      currentTimesheetId: timesheetId,
    });
  }, [
    initialProject,
    timesheetId,
    showTimeRange,
    effectiveAuftragsart,
    editor.heroEditableInThisTimesheet,
    customerSettingsReady,
  ]);

  const onHeroImport = (ids: string[]) => {
    void (async () => {
      if (!customerSettingsReady) return;
      const hp = initialProject.hero_project_id;
      if (!hp) return;
      const [rawTimes, usage] = await Promise.all([
        getHeroTimes(hp),
        getHeroUsage(initialProject.id),
      ]);

      const editableBaseline = ensureHeroEditableBaseline(
        rowsRef.current,
        heroLegacyAggregatesRef.current,
        editor.heroEditableInThisTimesheet,
      );

      const { items } = prepareHeroTimesForSuggestions(rawTimes, {
        auftragsart: effectiveAuftragsart,
        showTimeRange,
        usage,
        editableInThisTimesheet: editableBaseline,
      });

      const selected = items.filter((t) => t._uuid && ids.includes(t._uuid));
      const added = importHeroTimesToRows(selected, {
        auftragsart: effectiveAuftragsart,
        usage,
        editableInThisTimesheet: editableBaseline,
        showTimeRange,
        existingRows: rowsRef.current,
        locallyFreed: editor.heroLocallyFreed,
      });
      editor.appendRows(added);
    })();
  };

  const handleRemoveRow = (index: number) => {
    const row = editor.rows[index];
    if (row?.hero_uuid) {
      editor.setHeroLocallyFreed((prev) => new Set(prev).add(row.hero_uuid!));
    }
    if (row?.employee_time_id != null) {
      editor.setEmployeeTimeLocallyFreed((prev) => new Set(prev).add(Number(row.employee_time_id)));
    }
    editor.removeRow(index);
  };

  const handleRemoveDisplayedRow = (indices: number[]) => {
    const unique = [...new Set(indices)].sort((left, right) => right - left);
    for (const index of unique) handleRemoveRow(index);
  };

  const openAddTimeRow = () => setTimeRowDialog({ open: true, index: null });
  const openEditTimeRow = (index: number) => setTimeRowDialog({ open: true, index });
  const closeTimeRowDialog = () => setTimeRowDialog({ open: false, index: null });

  const handleTimeRowSave = (row: TimesheetRowRecord, mode: "create" | "edit") => {
    if (mode === "edit" && timeRowDialog.index != null) {
      editor.updateRow(timeRowDialog.index, row);
    } else {
      editor.addRow(row);
    }
  };

  const openAddMaterial = () => setMaterialDialog({ open: true, index: null });
  const openEditMaterial = (index: number) => setMaterialDialog({ open: true, index });
  const closeMaterialDialog = () => setMaterialDialog({ open: false, index: null });

  const handleMaterialSave = (material: TimesheetMaterialRecord, mode: "create" | "edit") => {
    if (mode === "edit" && materialDialog.index != null) {
      editor.updateMaterial(materialDialog.index, material);
    } else {
      editor.addMaterial(material);
    }
  };

  const fetchAppTimeItems = useCallback(async (): Promise<SuggestionItem[]> => {
    const [list, usageById] = await Promise.all([
      getAppTimes(initialProject.id),
      getAppTimeUsage(initialProject.id),
    ]);

    const editableBaseline = new Set(editor.employeeTimeEditableInThisTimesheet);
    if (editableBaseline.size === 0) {
      for (const row of rowsRef.current) {
        if (row.employee_time_id != null) {
          editableBaseline.add(Number(row.employee_time_id));
        }
      }
    }

    const usedInDraft = new Set<number>();
    for (const row of rowsRef.current) {
      if (row.employee_time_id != null) {
        usedInDraft.add(Number(row.employee_time_id));
      }
    }

    return buildAppTimeSuggestionItems(list, {
      usageById,
      editableInThisTimesheet: editableBaseline,
      locallyFreed: employeeTimeLocallyFreedRef.current,
      usedInCurrentDraft: usedInDraft,
      currentTimesheetId: timesheetId,
    });
  }, [
    initialProject.id,
    timesheetId,
    editor.employeeTimeEditableInThisTimesheet,
  ]);

  const onAppTimeImport = (ids: string[]) => {
    void (async () => {
      const [list, usageById] = await Promise.all([
        getAppTimes(initialProject.id),
        getAppTimeUsage(initialProject.id),
      ]);

      const editableBaseline = new Set(editor.employeeTimeEditableInThisTimesheet);
      if (editableBaseline.size === 0) {
        for (const row of rowsRef.current) {
          if (row.employee_time_id != null) {
            editableBaseline.add(Number(row.employee_time_id));
          }
        }
      }

      const selected = list.filter((n) => ids.includes(String(n.id)));
      const added = importAppTimesToRows(selected, {
        usageById,
        editableInThisTimesheet: editableBaseline,
        existingRows: rowsRef.current,
        locallyFreed: editor.employeeTimeLocallyFreed,
        currentTimesheetId: timesheetId,
      });
      editor.appendRows(added);
    })();
  };

  const buildPayload = (status: string) => {
    const period = editor.period;
    const dateFrom = editor.dateFrom || period?.dateFrom;
    const dateTo = editor.dateTo || period?.dateTo;
    if (!dateFrom || !dateTo) throw new Error("Zeitraum fehlt.");
    return {
      payload: {
        auftrag_id: initialProject.id,
        hero_project_id: initialProject.hero_project_id ?? null,
        date_from: dateFrom,
        date_to: dateTo,
        work_description: editor.workDescription || null,
        material_note: null,
        travel_km: useTravelEighths
          ? null
          : editor.travelKm
            ? parseFloat(editor.travelKm.replace(",", "."))
            : null,
        travel_eighths: useTravelEighths
          ? (() => {
              const raw = editor.travelEighths.trim();
              if (!raw) return null;
              const parsed = parseFloat(raw.replace(",", "."));
              return Number.isFinite(parsed) ? Math.max(0, Math.ceil(parsed)) : null;
            })()
          : null,
        travel_eighths_overridden: useTravelEighths ? editor.travelEighthsOverridden : null,
        time_rows_sort_key: timeSort.key,
        time_rows_sort_dir: timeSort.direction,
        status,
      },
      rows: sortTimesheetTimeEntries(
        editor.rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row.work_date && row.person_name),
        timeSort,
      ).map(({ row }) => row),
      materials: editor.materials,
    };
  };

  const persist = async (status: string): Promise<number> => {
    if (!customerSettingsReady) {
      throw new Error("Kundeneinstellungen werden noch geladen – bitte kurz warten.");
    }
    if (!editor.rows.length) throw new Error("Mindestens eine Stundenzeile erforderlich.");
    if (status === "ready") {
      const matErr = validateMaterialsForReady(editor.materials);
      if (matErr) throw new Error(matErr);
    }
    const body = buildPayload(status);
    let id = timesheetId;
    if (id) {
      await updateTimesheet(id, body.payload, body.rows, body.materials);
    } else {
      const res = await createTimesheet(body.payload, body.rows, body.materials);
      id = res.id;
      editor.setTimesheet({ id, status });
    }
    let aggregates = heroLegacyAggregatesRef.current;
    const heroProjectId = initialProject.hero_project_id;
    if (
      heroProjectId &&
      body.rows.some((row) => row.hero_uuid || row.hero_row_key || row.hero_uuids?.length)
    ) {
      // Resolve aggregate keys to real tracking UUIDs even if suggestions were never opened.
      if (!aggregates.length) {
        const rawTimes = await getHeroTimes(heroProjectId);
        aggregates = aggregateHeroTimesLegacy(rawTimes, showTimeRange);
        heroLegacyAggregatesRef.current = aggregates;
      }
    }
    await postHeroUsage(id, collectHeroUsageFromRows(body.rows, aggregates));
    return id;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      const id = await persist("draft");
      onSaved(id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReady = async () => {
    setSaving(true);
    setError(null);
    try {
      const matErr = validateMaterialsForReady(editor.materials);
      if (matErr) throw new Error(matErr);
      const id = await persist("draft");
      setFinalizeTimesheetId(id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewError(null);
    setPreviewBlob(null);
  };

  const handlePreview = async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    setError(null);
    try {
      if (!editor.rows.length) throw new Error("Mindestens eine Stundenzeile erforderlich.");
      const body = buildPayload("draft");
      const blob = await fetchTimesheetPreviewPdf(body.payload, body.rows, body.materials);
      setPreviewBlob(asPdfBlob(blob));
      setPreviewOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleCreateFromPreview = () => {
    closePreview();
    void handleSaveReady();
  };

  const projectCloseContext = useMemo(
    () =>
      finalizeTimesheetId
        ? {
            auftragId: initialProject.id,
            baseTimesheetId: editor.timesheet?.base_timesheet_id ?? finalizeTimesheetId,
          }
        : null,
    [finalizeTimesheetId, initialProject.id, editor.timesheet?.base_timesheet_id],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  const periodHint =
    editor.dateFrom && editor.dateTo
      ? `Zeitraum: ${editor.dateFrom === editor.dateTo ? editor.dateFrom : `${editor.dateFrom} – ${editor.dateTo}`}`
      : null;
  const ortLine = formatTimesheetOrt(initialProject.ort, initialProject.ort_zusatz);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1 timesheet-flow-scroll max-sm:pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-separator/80 bg-fill-quaternary/35 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">Projekt</p>
            <p className="truncate text-base font-semibold text-label">{initialProject.auftragsnummer}</p>
            <p className="truncate text-sm text-label-secondary">
              {initialProject.kunde}
              {ortLine ? ` · ${ortLine}` : ""}
            </p>
            {periodHint ? <p className="mt-1 text-xs text-label-tertiary">{periodHint}</p> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-xl bg-card px-3 py-2 text-right shadow-sm ring-1 ring-separator/80">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">Summe</p>
              <p className="text-lg font-semibold tabular-nums leading-tight text-label">{totalHoursLabel}</p>
            </div>
            {onChangeProject ? (
              <Button type="button" size="sm" variant="secondary" onClick={onChangeProject}>
                Projekt ändern
              </Button>
            ) : null}
          </div>
        </div>

        <TimesheetFlowCard title="Zeiten" titleClassName="mb-3 text-sm">
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,18rem),1fr))]">
            <TimesheetInlineSuggestionPanel
              title="Vorschläge aus Hero"
              hint="Zeiten aus Hero, gruppiert nach Tag und Mitarbeiter."
              loadItems={fetchHeroItems}
              reloadKey={`hero-${suggestionReloadKey}`}
              draftUsedIds={heroUsedInDraft}
              locallyFreedIds={editor.heroLocallyFreed}
              hideWhenEmpty
              onImportSelected={onHeroImport}
              onImportAll={onHeroImport}
            />
            <TimesheetInlineSuggestionPanel
              title="Vorschläge aus Zeiterfassung"
              hint="In der App erfasste Arbeitszeiten für dieses Projekt."
              loadItems={fetchAppTimeItems}
              reloadKey={`app-${suggestionReloadKey}`}
              draftUsedIds={appTimeUsedInDraft}
              locallyFreedIds={appTimeLocallyFreed}
              onImportSelected={onAppTimeImport}
              onImportAll={onAppTimeImport}
            />
          </div>
          <div className="mt-4 space-y-3 border-t border-separator/60 pt-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-label">Zeiten im Nachweis</h4>
                <p className="mt-0.5 text-xs text-label-secondary">
                  {showTimeRange
                    ? "Uhrzeiten erscheinen im PDF, weil sie beim Kunden aktiviert sind."
                    : "Uhrzeiten sind ausgeblendet. Mehrere Einsätze derselben Person an einem Tag werden zu einer Zeile mit addierten Stunden zusammengefasst."}
                </p>
              </div>
              <p className="text-sm text-label-secondary">
                {sortedTimeEntries.length} {sortedTimeEntries.length === 1 ? "Zeile" : "Zeilen"}
              </p>
            </div>
            <TimesheetTimeSortBar sort={timeSort} showTimeRange={showTimeRange} onChange={setTimeSort} />
            <TimesheetEditorTableShell empty={editor.rows.length === 0} emptyText="Noch keine Zeiten erfasst.">
              <TimesheetEditorTable>
                <TimesheetEditorTableHead>
                  <TimesheetEditorTableHeadCell>
                    <SortableHeader
                      label="Mitarbeiter"
                      columnKey="person_name"
                      sort={timeSort}
                      onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                    />
                  </TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell>
                    <SortableHeader
                      label="Datum"
                      columnKey="work_date"
                      sort={timeSort}
                      onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                    />
                  </TimesheetEditorTableHeadCell>
                  {showTimeRange ? (
                    <>
                      <TimesheetEditorTableHeadCell>
                        <SortableHeader
                          label="Von"
                          columnKey="time_from"
                          sort={timeSort}
                          onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                        />
                      </TimesheetEditorTableHeadCell>
                      <TimesheetEditorTableHeadCell>
                        <SortableHeader
                          label="Bis"
                          columnKey="time_to"
                          sort={timeSort}
                          onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                        />
                      </TimesheetEditorTableHeadCell>
                    </>
                  ) : null}
                  <TimesheetEditorTableHeadCell align="end">
                    <SortableHeader
                      label="Stunden"
                      columnKey="hours_decimal"
                      sort={timeSort}
                      onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                    />
                  </TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell>
                    <SortableHeader
                      label="Beschreibung"
                      columnKey="comment"
                      sort={timeSort}
                      onSort={(key) => setTimeSort(toggleTimesheetTimeSort(timeSort, key))}
                    />
                  </TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell className="w-[4.75rem]" />
                </TimesheetEditorTableHead>
                <TimesheetEditorTableBody>
                  {sortedTimeEntries.map((entry) => {
                    const sourceIndices = entry.sourceIndices;
                    const combined = sourceIndices.length > 1;
                    return (
                      <TimesheetTimeRowDisplay
                        key={sourceIndices.join("-")}
                        row={entry.row}
                        showTimeRange={showTimeRange}
                        editDisabled={combined}
                        editLabel={combined ? "Zusammengefasste Einsätze" : "Zeitzeile bearbeiten"}
                        onEdit={() => {
                          if (sourceIndices[0] == null || combined) return;
                          openEditTimeRow(sourceIndices[0]);
                        }}
                        onRemove={() => handleRemoveDisplayedRow(sourceIndices)}
                      />
                    );
                  })}
                </TimesheetEditorTableBody>
                <TimesheetEditorTableFoot>
                  <tr>
                    <td
                      colSpan={showTimeRange ? 4 : 2}
                      className="px-3 py-2.5 text-end text-[11px] font-semibold uppercase tracking-wide text-label-secondary"
                    >
                      Summe
                    </td>
                    <td className="px-3 py-2.5 text-end font-mono text-sm font-semibold tabular-nums text-label">
                      {formatTimesheetHours(totalHours)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </TimesheetEditorTableFoot>
              </TimesheetEditorTable>
            </TimesheetEditorTableShell>
            <div className="pt-4">
              <Button type="button" size="sm" variant="secondary" data-tour="stundennachweis-zeit" className="w-full sm:w-auto" onClick={openAddTimeRow}>
                Manuelle Zeile hinzufügen
              </Button>
            </div>
          </div>
        </TimesheetFlowCard>

        <TimesheetFlowCard title="Material" titleClassName="mb-3 text-sm">
          {standardMaterialHint ? (
            <p className="mb-3 text-xs text-label-secondary">{standardMaterialHint}</p>
          ) : null}
          <TimesheetInlineSuggestionPanel
            title="Bestellte / bereitgelegte Materialien"
            hint="Material aus Bestellungen und im Lager bereitgelegte Materialanforderungen."
            loadItems={fetchOrderedMaterialItems}
            reloadKey={`ord-mat-${suggestionReloadKey}-${editor.materials.length}`}
            draftUsedIds={materialsSuggestionValuesInDraft}
            onImportSelected={onOrderedMaterialImport}
            onImportAll={onOrderedMaterialImport}
          />
          {showFestpreisSuggestions ? (
            <div className="mt-3">
              <TimesheetInlineSuggestionPanel
                title="Festpreis aus Angebot"
                hint="Festpreispositionen aus dem App-Angebot bzw. der Auftragsbestätigung übernehmen."
                loadItems={fetchFestpreisMaterialItems}
                reloadKey={`fp-mat-${suggestionReloadKey}-${editor.materials.length}`}
                draftUsedIds={materialsSuggestionValuesInDraft}
                onImportSelected={onFestpreisMaterialImport}
                onImportAll={onFestpreisMaterialImport}
              />
            </div>
          ) : null}
          <div className="mt-4 border-t border-separator/60 pt-4">
            <h4 className="mb-3 text-sm font-semibold text-label">Material im Nachweis</h4>
            <TimesheetEditorTableShell
              empty={editor.materials.length === 0}
              emptyText="Noch kein Material erfasst."
            >
              <TimesheetEditorTable>
                <TimesheetEditorTableHead>
                  <TimesheetEditorTableHeadCell className="w-[10%]">Menge</TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell className="w-[10%]">Einheit</TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell className="hidden md:table-cell">EAN</TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell className="hidden lg:table-cell">Artikelnummer</TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell>Bezeichnung</TimesheetEditorTableHeadCell>
                  <TimesheetEditorTableHeadCell className="w-[4.75rem]" />
                </TimesheetEditorTableHead>
                <TimesheetEditorTableBody>
                  {editor.materials.map((m, i) => (
                    <TimesheetMaterialRow
                      key={i}
                      material={m}
                      onEdit={() => openEditMaterial(i)}
                      onRemove={() => editor.removeMaterial(i)}
                    />
                  ))}
                </TimesheetEditorTableBody>
              </TimesheetEditorTable>
            </TimesheetEditorTableShell>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="mt-4 w-full sm:w-auto"
              onClick={openAddMaterial}
            >
              Materialposition hinzufügen
            </Button>
          </div>
        </TimesheetFlowCard>

        <TimesheetFlowCard title="Ausgeführte Arbeiten" titleClassName="mb-3 text-sm">
          <FieldRow
            label="Kurzbeschreibung der Arbeiten im Zeitraum"
            hint="Diese Beschreibung erscheint oben auf dem Stundennachweis."
            fieldId="timesheet-work-description"
          >
            <AutoResizeTextarea
              id="timesheet-work-description"
              value={editor.workDescription}
              minRows={4}
              onChange={(e) => {
                workDescriptionAssist.onUserEdit();
                editor.setWorkDescription(e.target.value);
              }}
            />
          </FieldRow>
          <WorkDescriptionAssistPanel
            loading={workDescriptionAssist.loading}
            suggestedText={workDescriptionAssist.suggestedText}
            reason={workDescriptionAssist.reason}
            onApply={() =>
              workDescriptionAssist.applySuggestion((next) => editor.setWorkDescription(next))
            }
          />
          {useTravelEighths ? (
            <TextInput
              label="An- und Abfahrtspauschale (Achtel)"
              value={editor.travelEighths}
              className="mt-3"
              onChange={(e) => {
                editor.setTravelEighths(e.target.value);
                editor.setTravelEighthsOverridden(true);
              }}
            />
          ) : (
            <TextInput label="Fahrtkilometer" value={editor.travelKm} className="mt-3" onChange={(e) => editor.setTravelKm(e.target.value)} />
          )}
        </TimesheetFlowCard>

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

      </div>

      <TimesheetFlowFooter className="max-sm:pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <TimesheetFlowFooterButton type="button" variant="secondary" onClick={onCancel} disabled={saving || previewBusy}>
          Abbrechen
        </TimesheetFlowFooterButton>
        {initialTimesheetId ? (
          <TimesheetFlowFooterButton type="button" variant="secondary" onClick={onBack} disabled={saving || previewBusy}>
            Zurück
          </TimesheetFlowFooterButton>
        ) : null}
        <TimesheetFlowFooterButton type="button" variant="secondary" data-tour="stundennachweis-entwurf" onClick={() => void handleSaveDraft()} disabled={saving || previewBusy}>
          Als Entwurf speichern
        </TimesheetFlowFooterButton>
        <TimesheetFlowFooterButton
          type="button"
          variant="secondary"
          onClick={() => void handlePreview()}
          disabled={saving || previewBusy || !editor.rows.length}
        >
          {previewBusy ? "Vorschau …" : "Vorschau"}
        </TimesheetFlowFooterButton>
        <TimesheetFlowFooterButton type="button" onClick={() => void handleSaveReady()} disabled={saving || previewBusy}>
          Fertigstellen
        </TimesheetFlowFooterButton>
      </TimesheetFlowFooter>

      <TimesheetTimeRowDialog
        open={timeRowDialog.open}
        editRow={timeRowDialog.index != null ? editor.rows[timeRowDialog.index] ?? null : null}
        showTimeRange={showTimeRange}
        onClose={closeTimeRowDialog}
        onSave={handleTimeRowSave}
      />
      <TimesheetMaterialDialog
        open={materialDialog.open}
        editMaterial={materialDialog.index != null ? editor.materials[materialDialog.index] ?? null : null}
        onClose={closeMaterialDialog}
        onSave={handleMaterialSave}
      />

      <TimesheetFinalizeFlow
        timesheetId={finalizeTimesheetId}
        projectCloseContext={projectCloseContext}
        onComplete={(id) => {
          setFinalizeTimesheetId(null);
          if (onOpenActions) {
            onOpenActions(id);
          } else {
            onSaved(id);
            onOpenDetail?.(id);
          }
        }}
        onDismiss={() => setFinalizeTimesheetId(null)}
      />

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        title="Stundennachweis-Entwurf"
        description="QR-Codes sind Platzhalter und nicht zum Unterschreiben gültig."
        size="3xl"
        zIndexClass={layers.overlay}
        fullScreenMobile
        panelClassName="flex max-h-[min(92vh,56rem)] flex-col"
        bodyClassName="flex min-h-0 flex-1 flex-col p-0"
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={closePreview} disabled={saving}>
              Schließen
            </Button>
            <Button type="button" onClick={handleCreateFromPreview} disabled={saving || previewBusy}>
              Fertigstellen
            </Button>
          </div>
        }
      >
        {previewError ? (
          <p className="p-4 text-sm text-status-red">{previewError}</p>
        ) : null}
        {!previewBlob ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <InlinePdfView
            blob={previewBlob}
            title="Stundennachweis-Entwurf PDF"
            className="min-h-[min(70vh,40rem)] w-full flex-1"
          />
        )}
      </Dialog>
    </div>
  );
}
